import { lazy, Suspense } from 'react';

import { useSearchModal } from '@/components/shared/SearchModal/useSearchModal';

/**
 * Renders the search modal, and only once something has opened it.
 *
 * <h2>Why `lazy()` on its own did nothing</h2>
 *
 * The modal was imported eagerly, so its whole subtree sat in the chunk every route
 * waits for. Making the import `lazy()` looked like the fix and was not: the
 * component was still *rendered* unconditionally, and React fetches a lazy
 * component the moment it is rendered. The chunk was split and then immediately
 * requested anyway — a smaller main bundle and exactly the same wait.
 *
 * <p>Deferring an import only helps if something also defers the render. Here that
 * is `isVisible`, which the provider already tracks: most visitors never open this,
 * and the ones who do are already waiting for a search.
 *
 * <p>The provider itself stays eager — anything below may read the context, and it
 * is small. It is imported from its own module rather than the folder's barrel,
 * because the barrel re-exports the modal and would drag it back in.
 */
export function SearchModalMount() {
  const { modalState } = useSearchModal();

  if (!modalState.isVisible) {
    return null;
  }
  // No fallback: the modal animates itself in, and a spinner in front of a spinner
  // is worse than a few frames of nothing.
  return (
    <Suspense fallback={null}>
      <SearchModal />
    </Suspense>
  );
}

const SearchModal = lazy(() =>
  import('@/components/shared/SearchModal/SearchModal').then((m) => ({ default: m.SearchModal })),
);
